import type { PromoModalType } from "@faire/web-api/indigofair/data/PromoModalType";
import { route as boardsRoute } from "@faire/web-api/routes/boards";
import { route as followingBoardsRoute } from "@faire/web-api/routes/boards/following";
import { route as brandInvoiceTokenRoute } from "@faire/web-api/routes/brand-invoice/brandInvoiceToken";
import { route as brandRoute } from "@faire/web-api/routes/brand/brandToken";
import { route as brandAppLandingPageRoute } from "@faire/web-api/routes/brands/app";
import { route as careersRoute } from "@faire/web-api/routes/careers";
import { route as c1Route } from "@faire/web-api/routes/category/c1CategoryUrlId";
import { route as c2Route } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { route as c3Route } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId";
import { route as confirmIdentityRoute } from "@faire/web-api/routes/confirm-identity";
import { route as expandingBusinessesOfferRoute } from "@faire/web-api/routes/expanding-businesses-offer";
import { route as expandingBusinessesOfferApplyRoute } from "@faire/web-api/routes/expanding-businesses-offer/apply";
import { route as netTermsOpenWithFaireApplicationRoute } from "@faire/web-api/routes/net-terms/owf-application";
import { route as openWithFaireRoute } from "@faire/web-api/routes/open-with-faire";
import { route as pointOfSaleOnboardingRoute } from "@faire/web-api/routes/point-of-sale/onboarding";
import { route as pointOfSaleOrderManagerRoute } from "@faire/web-api/routes/point-of-sale/order-manager/orderToken";
import { route as collectionRoute } from "@faire/web-api/routes/product-based-collection/product_based_collection_token";
import { route as productRoute } from "@faire/web-api/routes/product/productToken";
import { route as retailerSettingsInsiderMembershipRoute } from "@faire/web-api/routes/retailer/settings/insider-membership";
import { route as paymentMethodsRoute } from "@faire/web-api/routes/retailer/settings/payment-methods";
import { route as homeRoute } from "@faire/web-api/routes/root";
import { route as searchRoute } from "@faire/web-api/routes/search";
import { route as smallBusinessGrantRoute } from "@faire/web-api/routes/small-business-grant";
import { route as insiderTermsOfServiceRoute } from "@faire/web-api/routes/tos-insider";
import { route as unpaidInvoicesRoute } from "@faire/web-api/routes/user/account/invoices/unpaid";
import { route as ordersRoute } from "@faire/web-api/routes/user/account/orders";
import { makeObservable } from "@faire/web/common/makeObservable";
import { getCurrentPath } from "@faire/web/common/Path";
import { pathMatchesRoute } from "@faire/web/common/routes/RouteMatcher";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { matchPathWithLocale } from "@faire/web/ui/routing/react-utils";
import { action, computed, observable } from "mobx";
import { matchPath } from "react-router-dom";

import { isE2E } from "@faire/retailer/lib/isE2E";
import { isFOIModalE2E } from "@faire/retailer/lib/isFOIModalE2E";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { home } from "@faire/retailer/paths";
import { getPromoModalInformation } from "@faire/retailer/serialized-data/getPromoModalInformation";
import { getSettingInsiderTermsAndConditionsEnabled } from "@faire/retailer/settings/getSettingInsiderTermsAndConditionsEnabled";
import { getSettingInsiderTermsAndConditionsForcedAcceptanceEnabled } from "@faire/retailer/settings/getSettingInsiderTermsAndConditionsForcedAcceptanceEnabled";
import { FirstOrderIncentiveStore } from "@faire/retailer/stores/domain/FirstOrderIncentiveStore";
import { MilestonesStore } from "@faire/retailer/stores/domain/MilestonesStore";
import { OpeningOrderIncentivesStore } from "@faire/retailer/stores/domain/OpeningOrderIncentivesStore";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

export const MOBILE_APP_PROMOTION_ALLOWED_PATHS = [
  searchRoute,
  brandRoute,
  productRoute,
  ordersRoute,
  c1Route,
  c2Route,
  c3Route,
  boardsRoute,
  followingBoardsRoute,
  homeRoute,
  collectionRoute,
];

export const MOBILE_APP_PROMOTION_BLOCKED_PATHS = [
  "/retailer/:retailerToken/category/International%20Best%20Sellers",
  "/category/International%20Best%20Sellers",
];

const PROMOTION_BLOCKED_PATHS = [
  retailerSettingsInsiderMembershipRoute,
  confirmIdentityRoute,
  careersRoute,
  brandAppLandingPageRoute,
  insiderTermsOfServiceRoute,
  pointOfSaleOnboardingRoute,
  pointOfSaleOrderManagerRoute,
  brandInvoiceTokenRoute,
  expandingBusinessesOfferRoute,
  expandingBusinessesOfferApplyRoute,
  smallBusinessGrantRoute,
  openWithFaireRoute,
];

const OVERDUE_INVOICES_BLOCKED_PATHS = [
  paymentMethodsRoute,
  unpaidInvoicesRoute,
];

const isCurrentPathInList = (list: Array<string>): boolean => {
  return list.some(
    (path) => !!matchPath(getCurrentPath().pathname, { path, exact: true })
  );
};

// Don't export this to UI code, because it's not safe to use storage in SSR.
// Create wrapper hooks instead.
export class PromoModalStore {
  constructor() {
    makeObservable(this);
  }

  /**
   * @trackfunction
   */
  static get = singletonGetter(PromoModalStore);

  @observable
  hasUserClosedModal: boolean = false;

  @computed
  private get modalType() {
    return getPromoModalInformation().type;
  }

  @computed
  private get isInOpenWithFaireApplicationPage() {
    return !!pathMatchesRoute(
      getCurrentPath().pathname,
      netTermsOpenWithFaireApplicationRoute
    );
  }

  @computed
  private get onHomePage(): boolean {
    return !!matchPath(getCurrentPath().pathname, {
      path: home(),
      exact: true,
    });
  }

  @computed
  get shouldDisplayFirstOrderIncentiveModal() {
    const requestedToDisplay = this.modalType === "FIRST_ORDER_INCENTIVE_MODAL";

    return (
      requestedToDisplay &&
      FirstOrderIncentiveStore.get().shouldShowFirstOrderIncentiveModal &&
      // this causes issues in E2Es so it's disabled by default but may be enabled
      // Slack thread: https://faire-wholesale.slack.com/archives/C013Q8WTZLM/p1681246854989139
      (!isE2E() || isFOIModalE2E())
    );
  }

  @computed
  private get shouldDisplayFaireDirectFirstOrderIncentiveModal(): boolean {
    return (
      this.modalType === "FAIRE_DIRECT_INCENTIVE_MODAL" && isLoggedInRetailer()
    );
  }

  @computed
  private get shouldDisplayFaireDirectMarketplaceIncentiveModal(): boolean {
    //marketplace incentive modal is gated in usePromoModalToDisplay to access hooks
    return (
      (this.modalType === "FAIRE_DIRECT_MARKETPLACE_DISCOUNT_MODAL" ||
        this.modalType ===
          "FAIRE_DIRECT_EVERGREEN_MARKETPLACE_DISCOUNT_MODAL") &&
      isLoggedInRetailer()
    );
  }

  @computed
  private get shouldDisplayPastDueInvoicesModal(): boolean {
    const isLocationAllowed = isCurrentPathInList(
      OVERDUE_INVOICES_BLOCKED_PATHS
    );
    const requestedToDisplay = this.modalType === "PAST_DUE_INVOICE_MODAL";
    return isLoggedInRetailer() && !isLocationAllowed && requestedToDisplay;
  }

  @computed
  private get shouldDisplayPostOrderInsiderEnrollmentPromoModal() {
    return (
      isLoggedInRetailer() && this.modalType === "POST_ORDER_INSIDER_ENROLLMENT"
    );
  }

  @computed
  private get shouldDisplayOpeningOrderIncentivesModal() {
    const {
      showOpeningOrderIncentives,
      numberOfHoursRemaining,
      isDrivenFromMarketingEmail,
    } = OpeningOrderIncentivesStore.get();
    if (!numberOfHoursRemaining) {
      return false;
    }
    return (
      isLoggedInRetailer() &&
      showOpeningOrderIncentives &&
      !MilestonesStore.get().hasMilestoneOccurred(
        "OPENING_ORDER_DISCOUNT_MODAL_SEEN"
      ) &&
      numberOfHoursRemaining >= 48 &&
      !isDrivenFromMarketingEmail
    );
  }

  @computed
  private get shouldDisplayOpeningOrderIncentivesCountdownModal() {
    const {
      showOpeningOrderIncentives,
      numberOfHoursRemaining,
      isDrivenFromMarketingEmail,
    } = OpeningOrderIncentivesStore.get();
    if (!numberOfHoursRemaining) {
      return false;
    }
    return (
      isLoggedInRetailer() &&
      showOpeningOrderIncentives &&
      !MilestonesStore.get().hasMilestoneOccurred(
        "OPENING_ORDER_DISCOUNT_COUNTDOWN_MODAL_SEEN"
      ) &&
      numberOfHoursRemaining < 48 &&
      !isDrivenFromMarketingEmail
    );
  }

  @computed
  private get shouldDisplayPaymentDisputeModal() {
    return isLoggedInRetailer() && this.modalType === "PAYMENT_DISPUTE_MODAL";
  }

  @computed
  private get shouldDisplayPrepaidCardsRestrictedModal() {
    return (
      isLoggedInRetailer() &&
      !MilestonesStore.get().hasMilestoneOccurred(
        "PREPAID_CARDS_RESTRICTED_MODAL_SEEN"
      ) &&
      this.modalType === "PREPAID_CARDS_RESTRICTED_MODAL"
    );
  }

  @computed
  private get shouldDisplayAchPliLandingModal() {
    const isUpsellModalShowing = !!getCurrentPath().searchParams.get(
      "achProactiveLimitIncrease"
    );
    return (
      isLoggedInRetailer() &&
      !MilestonesStore.get().hasMilestoneOccurred(
        "HAS_SEEN_ACH_PROACTIVE_LIMIT_INCREASE_MODAL"
      ) &&
      !isUpsellModalShowing &&
      this.modalType === "ACH_PROACTIVE_LIMIT_INCREASE_LANDING_MODAL"
    );
  }

  @computed
  private get shouldDisplayAchTermsPreApprovalLandingModal() {
    const isAppModalShowing = !!getCurrentPath().searchParams.get(
      "achTermsPreApproval"
    );
    return (
      isLoggedInRetailer() &&
      !isAppModalShowing &&
      this.modalType === "ACH_TERMS_PRE_APPROVAL_LANDING_MODAL"
    );
  }

  @computed
  private get shouldDisplayTermsPreApprovalLandingModal() {
    const isAppModalShowing =
      !!getCurrentPath().searchParams.get("termsPreApproval");
    return (
      isLoggedInRetailer() &&
      !isAppModalShowing &&
      this.modalType === "TERMS_PRE_APPROVAL_LANDING_MODAL"
    );
  }

  @computed
  private get shouldDisplayTermsInitialLimitAssignmentModal() {
    const isAppModalShowing =
      !!getCurrentPath().searchParams.get("termsAssignment");

    return (
      isLoggedInRetailer() &&
      !isAppModalShowing &&
      this.modalType === "NON_BM_INITIAL_LIMIT_LANDING_MODAL"
    );
  }

  @computed
  private get isInBlockedPath() {
    return PROMOTION_BLOCKED_PATHS.some(
      (route) => !!matchPathWithLocale(getCurrentPath().pathname, route)
    );
  }

  @computed
  private get shouldDisplayWarehouseSaleModal() {
    const isLocationAllowed = this.onHomePage;

    return (
      isLoggedInRetailer() &&
      RetailerStore.get().showWarehouseFlashSale &&
      isLocationAllowed &&
      !MilestonesStore.get().hasMilestoneOccurred(
        "HAS_SEEN_WAREHOUSE_SALE_MODAL_NOV_22"
      ) &&
      this.modalType === "WAREHOUSE_SALE_MODAL"
    );
  }

  @computed
  private get shouldDisplayPastDueInvoicesV2Modal() {
    return isLoggedInRetailer() && this.modalType === "OVERDUE_INVOICE_MODAL";
  }

  @computed
  private get shouldDisplayPaymentPlanPaymentFailedModal() {
    return (
      isLoggedInRetailer() &&
      this.modalType === "PAYMENT_PLAN_PAYMENT_FAILED_MODAL"
    );
  }

  @computed
  private get shouldDisplayPaymentPlanFullyPaidModal() {
    return (
      isLoggedInRetailer() && this.modalType === "PAYMENT_PLAN_FULLY_PAID_MODAL"
    );
  }

  @computed
  private get shouldDisplayInsiderTermsAndConditionsReviewModal() {
    return (
      isLoggedInRetailer() &&
      UserStore.get().userCanPlaceOrder &&
      this.modalType === "INSIDER_TERMS_AND_CONDITIONS_MODAL" &&
      getSettingInsiderTermsAndConditionsEnabled()
    );
  }

  @computed
  private get shouldDisplayInsiderTermsAndConditionsAcceptanceModal() {
    return (
      isLoggedInRetailer() &&
      UserStore.get().userCanPlaceOrder &&
      this.modalType === "INSIDER_TERMS_AND_CONDITIONS_ACCEPTANCE" &&
      getSettingInsiderTermsAndConditionsForcedAcceptanceEnabled()
    );
  }

  @computed
  private get shouldDisplayInsiderExpandedCoverageModal() {
    return (
      isLoggedInRetailer() &&
      UserStore.get().userCanPlaceOrder &&
      this.modalType === "INSIDER_PARTNERSHIP_LAUNCH_ANNOUNCEMENT"
    );
  }

  @computed
  private get shouldDisplayInsiderRefreshReminderModal() {
    return (
      isLoggedInRetailer() &&
      this.modalType === "INSIDER_REFRESH_REMINDER_MODAL"
    );
  }

  @computed
  private get shouldDisplayShopifyNewRetailerBenefitsModal() {
    return (
      !MilestonesStore.get().hasMilestoneOccurred(
        "SHOPIFY_ONBOARDING_BENEFITS_MODAL_SEEN"
      ) && this.modalType === "SHOPIFY_NEW_RETAILER_BENEFITS_MODAL"
    );
  }

  @computed
  private get shouldDisplayShopifyExistingRetailerBenefitsModal() {
    return (
      !MilestonesStore.get().hasMilestoneOccurred(
        "SHOPIFY_ONBOARDING_BENEFITS_MODAL_SEEN"
      ) && this.modalType === "SHOPIFY_EXISTING_RETAILER_BENEFITS_MODAL"
    );
  }

  @computed
  private get shouldDisplayFairePayDefaultedModal() {
    return (
      isLoggedInRetailer() && this.modalType === "FAIRE_PAY_DEFAULTED_MODAL"
    );
  }

  @computed
  private get shouldDisplaySaleAnnouncementModal() {
    return (
      isLoggedInRetailer() &&
      this.modalType === "SALE_ANNONCEMENT_MODAL" &&
      !MilestonesStore.get().hasMilestoneOccurred("SALE_ANNOUNCEMENT_DISMISSED")
    );
  }

  /* eslint-disable complexity */
  @computed
  get modalToDisplay(): keyof typeof PromoModalType | null {
    if (
      this.isInBlockedPath ||
      this.hasUserClosedModal ||
      this.isInOpenWithFaireApplicationPage ||
      this.modalType === "PROTECTED_FOR_NEW_RETAILER"
    ) {
      return null;
    }

    if (this.shouldDisplayFirstOrderIncentiveModal) {
      return "FIRST_ORDER_INCENTIVE_MODAL";
    } else if (this.shouldDisplayFaireDirectFirstOrderIncentiveModal) {
      return "FAIRE_DIRECT_INCENTIVE_MODAL";
    } else if (this.shouldDisplayFaireDirectMarketplaceIncentiveModal) {
      return "FAIRE_DIRECT_MARKETPLACE_DISCOUNT_MODAL";
    } else if (this.shouldDisplayPaymentDisputeModal) {
      return "PAYMENT_DISPUTE_MODAL";
    } else if (this.shouldDisplayPastDueInvoicesModal) {
      return "PAST_DUE_INVOICE_MODAL";
    } else if (this.shouldDisplayPostOrderInsiderEnrollmentPromoModal) {
      return "POST_ORDER_INSIDER_ENROLLMENT";
    } else if (this.shouldDisplayOpeningOrderIncentivesModal) {
      return "OPENING_ORDER_DISCOUNT_MODAL";
    } else if (this.shouldDisplayOpeningOrderIncentivesCountdownModal) {
      return "OPENING_ORDER_DISCOUNT_COUNTDOWN_MODAL";
    } else if (this.shouldDisplayWarehouseSaleModal) {
      return "WAREHOUSE_SALE_MODAL";
    } else if (this.shouldDisplayPrepaidCardsRestrictedModal) {
      return "PREPAID_CARDS_RESTRICTED_MODAL";
    } else if (this.shouldDisplayPastDueInvoicesV2Modal) {
      return "OVERDUE_INVOICE_MODAL";
    } else if (this.shouldDisplayPaymentPlanPaymentFailedModal) {
      return "PAYMENT_PLAN_PAYMENT_FAILED_MODAL";
    } else if (this.shouldDisplayPaymentPlanFullyPaidModal) {
      return "PAYMENT_PLAN_FULLY_PAID_MODAL";
    } else if (this.shouldDisplayAchPliLandingModal) {
      return "ACH_PROACTIVE_LIMIT_INCREASE_LANDING_MODAL";
    } else if (this.shouldDisplayAchTermsPreApprovalLandingModal) {
      return "ACH_TERMS_PRE_APPROVAL_LANDING_MODAL";
    } else if (this.shouldDisplayTermsPreApprovalLandingModal) {
      return "TERMS_PRE_APPROVAL_LANDING_MODAL";
    } else if (this.shouldDisplayInsiderTermsAndConditionsAcceptanceModal) {
      return "INSIDER_TERMS_AND_CONDITIONS_ACCEPTANCE";
    } else if (this.shouldDisplayInsiderTermsAndConditionsReviewModal) {
      return "INSIDER_TERMS_AND_CONDITIONS_MODAL";
    } else if (this.shouldDisplayShopifyNewRetailerBenefitsModal) {
      return "SHOPIFY_NEW_RETAILER_BENEFITS_MODAL";
    } else if (this.shouldDisplayShopifyExistingRetailerBenefitsModal) {
      return "SHOPIFY_EXISTING_RETAILER_BENEFITS_MODAL";
    } else if (this.shouldDisplayFairePayDefaultedModal) {
      return "FAIRE_PAY_DEFAULTED_MODAL";
    } else if (this.shouldDisplayTermsInitialLimitAssignmentModal) {
      return "NON_BM_INITIAL_LIMIT_LANDING_MODAL";
    } else if (this.shouldDisplayInsiderExpandedCoverageModal) {
      return "INSIDER_PARTNERSHIP_LAUNCH_ANNOUNCEMENT";
    } else if (this.shouldDisplaySaleAnnouncementModal) {
      return "SALE_ANNONCEMENT_MODAL";
    } else if (this.shouldDisplayInsiderRefreshReminderModal) {
      return "INSIDER_REFRESH_REMINDER_MODAL";
    }
    return null;
  }

  @action
  setHasUserClosedModal = (closed: boolean) => {
    this.hasUserClosedModal = closed;
  };
}

// Don't export this to UI code, because it's not safe to use storage in SSR.
// Create wrapper hooks instead.
export const usePromoModalStore = createStoreHook(PromoModalStore);
